/**
 * A volume that reports the player walking into it.
 *
 * This exists for one line of Rapier configuration that is invisible when it is missing.
 * The player capsule is a `kinematicPosition` body and these sensors have no rigid body of
 * their own, so every player sensor in the game is a kinematic-versus-fixed pair — an
 * interaction class Rapier does not compute unless the collider asks for it by name. Leave
 * `KINEMATIC_FIXED` out and the sensor is not merely unreliable: it never fires, while
 * looking entirely correct in the scene and in the physics debug view.
 *
 * Two sensors have already been written without it — the maze's exit and the portal gate
 * that replaced it — so the knowledge lives here rather than in a comment for the next one
 * to miss. Sensors that watch for *objects* (`DropZone`) need none of this: a carried box
 * is a dynamic body, and dynamic pairs are computed by default.
 */

import { CuboidCollider, useRapier } from '@react-three/rapier';
import type { Box, Vec3 } from '../game/profile/types';
import { halfExtents, boxPosition } from './boxUnits';
import type { BodyUserData } from './useInteractionTarget';

type Props = {
  /** The volume to watch, in the profile's box format. */
  box: Box;
  /**
   * The player entered. Called once per entry, from Rapier's event queue, with the
   * position the player's body was at when it first overlapped this sensor — read
   * straight from Rapier rather than a ref, so the sensor never needs to know where the
   * player's body is kept.
   */
  onEnter: (playerPosition: Vec3) => void;
  /** The player left. Only worth passing where leaving means something. */
  onLeave?: () => void;
};

/** Whether the body that touched us is the player, and not a box that rolled in. */
function isPlayer(userData: unknown): boolean {
  const { kind } = (userData ?? {}) as Partial<BodyUserData>;
  return kind === 'player';
}

export function PlayerSensor({ box, onEnter, onLeave }: Props) {
  const { rapier } = useRapier();

  return (
    <CuboidCollider
      sensor
      args={halfExtents(box)}
      position={boxPosition(box)}
      activeCollisionTypes={
        rapier.ActiveCollisionTypes.DEFAULT | rapier.ActiveCollisionTypes.KINEMATIC_FIXED
      }
      onIntersectionEnter={({ other }) => {
        if (isPlayer(other.rigidBody?.userData)) onEnter(other.rigidBody!.translation());
      }}
      onIntersectionExit={({ other }) => {
        if (onLeave && isPlayer(other.rigidBody?.userData)) onLeave();
      }}
    />
  );
}
